import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar, { NavItem } from "./Sidebar";
import { Permission } from "../permissions/permission.enum";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import Button from "./ui/Button";
import { TEST_TOOLS_ENABLED } from "../config/api.config";
import {
  FaSun,
  FaMoon,
  FaGlobe,
  FaTachometerAlt,
  FaUsers,
  FaUserShield,
  FaUserTag,
  FaClipboardList,
  FaTools,

  FaBell,
  FaEnvelope,
  FaPaperPlane,
  FaFlask,
  FaCheckDouble,
  FaListAlt,
  FaWpforms,
  FaBuilding,
  FaCoins,
  FaQuestionCircle,
  FaIdCard,
  FaAt,
  FaMobileAlt,
  FaKey,
  FaRobot,
  FaHistory,
  FaTag,
  FaTicketAlt,
  FaCalendarAlt,
  FaCreditCard,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaPhoneAlt,
  FaBan,
} from "react-icons/fa";

interface LayoutProps {
  children: React.ReactNode;
  showBackButton?: boolean;
  title?: string;
  showSidebar?: boolean;
  menuPosition?: "left" | "top";
}

const Layout = ({
  children,
  showBackButton = false,
  title,
  showSidebar = true,
  menuPosition = "left",
}: LayoutProps) => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const toggleLanguage = () => {
    const nextLang = i18n.language === "en" ? "tc" : "en";
    i18n.changeLanguage(nextLang);
  };

  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("cms_sidebar_collapsed");
      if (stored === "true") setIsSidebarCollapsed(true);
      if (stored === "false") setIsSidebarCollapsed(false);
    } catch {
      setIsSidebarCollapsed(false);
    }
  }, []);

  const toggleSidebarCollapsed = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("cms_sidebar_collapsed", String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const navItems: NavItem[] = [
    { label: t("dashboard"), to: "/", icon: <FaTachometerAlt /> },
    {
      label: t("management"),
      type: "group",
      children: [
        {
          label: t("users"),
          to: "/users",
          permission: Permission.USERS_READ,
          icon: <FaUsers />,
        },
        {
          label: t("admins"),
          to: "/admins",
          permission: Permission.ADMINS_READ,
          icon: <FaUserShield />,
        },
        {
          label: t("roles"),
          to: "/roles",
          permission: Permission.ROLES_READ,
          icon: <FaUserTag />,
        },
        {
          label: t("companies"),
          to: "/companies",
          permission: Permission.LISTINGS_READ,
          icon: <FaBuilding />,
        },
      ],
    },
    {
      label: t("logs"),
      type: "group",
      children: [
        {
          label: t("audit_logs"),
          to: "/audit-logs",
          permission: Permission.AUDIT_LOGS_READ,
          icon: <FaClipboardList />,
        },
      ],
    },
    {
      label: t("system"),
      type: "group",
      children: [
        {
          label: t("maintenance"),
          to: "/maintenance",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaTools />,
        },
        {
          label: t("help_support"),
          to: "/system/help-support",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaQuestionCircle />,
        },
        {
          label: t("admin_email_settings"),
          to: "/system/admin-email",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaAt />,
        },
        {
          label: t("app_version_settings"),
          to: "/system/app-version",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaMobileAlt />,
        },
        {
          label: t("otp_fallback_settings"),
          to: "/system/otp-fallback",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaKey />,
        },
        {
          label: t("app_check_alert_settings"),
          to: "/system/app-check-alert",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaShieldAlt />,
        },
        {
          label: t("phone_regions"),
          to: "/system/phone-regions",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaPhoneAlt />,
        },
        {
          label: t("blocked_emails"),
          to: "/system/blocked-emails",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaBan />,
        },
      ],
    },
    {
      label: t("marketplace"),
      type: "group",
      children: [
        {
          label: t("listings"),
          to: "/listings",
          permission: Permission.LISTINGS_READ,
          icon: <FaListAlt />,
        },
        {
          // users.read — matches what GET /admin/listings/manage enforces.
          label: t("manageListings"),
          to: "/listings/manage",
          permission: Permission.USERS_READ,
          icon: <FaListAlt />,
        },
        {
          label: t("cv_review_nav"),
          to: "/cv-review",
          permission: Permission.USERS_VERIFICATION,
          icon: <FaIdCard />,
        },
        {
          label: t("bookings"),
          to: "/bookings",
          permission: Permission.BOOKINGS_READ,
          icon: <FaCalendarAlt />,
        },
        {
          label: t("form_config"),
          to: "/form-config",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaWpforms />,
        },
        {
          label: t("form_pricing_nav"),
          to: "/form-config/pricing",
          permission: Permission.FINANCE_READ,
          icon: <FaCoins />,
        },
        {
          label: t("topup_packages"),
          to: "/points/top-up-packages",
          permission: Permission.FINANCE_READ,
          icon: <FaCoins />,
        },
        {
          label: t("payment_transactions"),
          to: "/payment-transactions",
          permission: Permission.FINANCE_READ,
          icon: <FaCreditCard />,
        },
        {
          label: t("listing_categories"),
          to: "/listing-categories",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaListAlt />,
        },
        {
          label: t("popular_destinations"),
          to: "/charter/destinations",
          permission: Permission.CHARTER_DESTINATIONS_READ,
          icon: <FaMapMarkerAlt />,
        },
      ],
    },
    {
      label: t("promotions_nav"),
      type: "group",
      children: [
        {
          label: t("promotion_periods"),
          to: "/promotions/periods",
          permission: Permission.FINANCE_READ,
          icon: <FaTag />,
        },
        {
          label: t("promo_code_events"),
          to: "/promotions/codes",
          permission: Permission.FINANCE_READ,
          icon: <FaTicketAlt />,
        },
      ],
    },
    {
      label: t("notifications"),
      type: "group",
      children: [
        {
          label: t("send_notification"),
          to: "/notifications/send",
          permission: Permission.USER_NOTIFICATIONS_CREATE,
          icon: <FaPaperPlane />,
        },
        ...(TEST_TOOLS_ENABLED
          ? [
              {
                label: t("test_push"),
                to: "/notifications/test-push",
                permission: Permission.USER_NOTIFICATIONS_CREATE,
                icon: <FaFlask />,
              },
            ]
          : []),
        {
          label: t("notifications_sent"),
          to: "/notifications/sent",
          permission: Permission.ADMIN_NOTIFICATIONS_READ,
          icon: <FaCheckDouble />,
        },
        {
          label: t("admin_notifications"),
          to: "/notifications/admin",
          permission: Permission.ADMIN_NOTIFICATIONS_READ,
          icon: <FaBell />,
        },
        {
          label: t("user_notifications"),
          to: "/notifications/user",
          permission: Permission.USER_NOTIFICATIONS_READ,
          icon: <FaEnvelope />,
        },
      ],
    },
    {
      label: t("ai"),
      type: "group",
      children: [
        {
          label: t("ai_settings"),
          to: "/ai/settings",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaRobot />,
        },
        {
          label: t("ai_logs"),
          to: "/ai/logs",
          permission: Permission.SYSTEM_SETTINGS_READ,
          icon: <FaHistory />,
        },
      ],
    },
  ];

  const visibleNavItemsFlat = navItems
    .flatMap((item) => (item.children ? item.children : [item]))
    .filter((item) => !item.permission || hasPermission(item.permission));

  const getHeaderContent = (containerClassName: string) => (
    <div
      className={[
        containerClassName,
        "h-16 flex items-center justify-between",
      ].join(" ")}
    >
      <div className="flex items-center gap-4 min-w-0">
        {showBackButton && (
          <button
            onClick={handleBack}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("back")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
        )}
        {title && (
          <h1 className="text-xl font-semibold tracking-tight truncate">
            {title}
          </h1>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={toggleLanguage}
          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-1"
          aria-label={t("switch_language")}
          title={t("switch_language")}
        >
          <FaGlobe className="w-5 h-5" />
          <span className="text-sm font-medium">
            {i18n.language === "en" ? "EN" : "繁"}
          </span>
        </button>
        <button
          onClick={toggleTheme}
          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          aria-label={theme === "dark" ? t("light_mode") : t("dark_mode")}
          title={theme === "dark" ? t("light_mode") : t("dark_mode")}
        >
          {theme === "dark" ? (
            <FaSun className="w-5 h-5" />
          ) : (
            <FaMoon className="w-5 h-5" />
          )}
        </button>
        {isAuthenticated && (
          <Button variant="secondary" size="sm" onClick={logout}>
            {t("logout")}
          </Button>
        )}
      </div>
    </div>
  );

  const headerContent = getHeaderContent("w-full px-4 sm:px-6 lg:px-8");

  return (
    <div className="h-screen overflow-hidden bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-200 font-sans flex flex-col">
      {menuPosition === "left" ? (
        <div className="flex h-full overflow-hidden">
          {showSidebar && (
            <Sidebar
              items={navItems}
              collapsed={isSidebarCollapsed}
              onToggleCollapsed={toggleSidebarCollapsed}
            />
          )}

          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-10 flex-shrink-0">
              {headerContent}
            </header>

            <main className="flex-1 flex flex-col overflow-y-auto w-full px-4 sm:px-6 lg:px-8 py-8">
              {children}
            </main>
          </div>
        </div>
      ) : (
        <div className="flex flex-col min-h-screen">
          <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-10">
            {headerContent}
            {showSidebar && (
              <div className="border-t border-gray-200 dark:border-gray-700 overflow-x-auto">
                <nav className="w-full px-2 sm:px-4 lg:px-6 py-2 flex items-center gap-1 whitespace-nowrap">
                  {visibleNavItemsFlat.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to!}
                      className={({ isActive }) =>
                        [
                          "inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                          isActive
                            ? "bg-blue-50 text-blue-700 dark:bg-gray-700 dark:text-white"
                            : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700",
                        ].join(" ")
                      }
                    >
                      <span className="text-lg" aria-hidden>
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </nav>
              </div>
            )}
          </header>

          <main className="w-full px-4 sm:px-6 lg:px-8 py-8">{children}</main>
        </div>
      )}
    </div>
  );
};

export default Layout;
